<template>
  <div
    class="task-row group relative flex items-center gap-3 px-4 py-3.5 sm:py-3 cursor-pointer transition-colors duration-150"
    :class="{ 'task-row-created': justCreated }"
    :style="tintStyle"
    @click="emit('open', task)"
  >
    <Tooltip v-if="reorderable" text="Drag to reorder">
      <span
        class="task-row-handle flex-shrink-0 -ml-1 flex items-center justify-center w-4 h-5 text-gray-300 dark:text-gray-600 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-150 cursor-grab active:cursor-grabbing"
        @click.stop
        aria-hidden="true"
      >
        <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
          <circle cx="7" cy="5" r="1.3"/><circle cx="13" cy="5" r="1.3"/>
          <circle cx="7" cy="10" r="1.3"/><circle cx="13" cy="10" r="1.3"/>
          <circle cx="7" cy="15" r="1.3"/><circle cx="13" cy="15" r="1.3"/>
        </svg>
      </span>
    </Tooltip>

    <button
      type="button"
      @click.stop="emit('toggle', task, $event)"
      @mouseenter="hovering = true"
      @mouseleave="hovering = false"
      class="flex-shrink-0 w-6 h-6 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center transition-all duration-150 ease-out active:scale-90 sm:hover:scale-110"
      :style="checkboxStyle"
      :aria-label="isDone ? 'Mark as not done' : 'Mark as done'"
    >
      <Transition name="check-pop">
        <svg v-if="isDone" class="w-3.5 h-3.5 sm:w-3 sm:h-3 text-white" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M1.5 5l2.5 2.5 4.5-4.5"/>
        </svg>
      </Transition>
    </button>

    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-1.5">
        <!-- Had no label of any kind before: the one icon on this row whose
             meaning you genuinely could not guess. -->
        <Tooltip v-if="task.is_recurring" text="One occurrence of a repeating task">
          <span class="flex-shrink-0 flex items-center text-gray-400" role="img" aria-label="One occurrence of a repeating task">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
            </svg>
          </span>
        </Tooltip>
        <!-- Why this row is in Today when its due date says otherwise.
             Double chevron, deliberately nothing like the circular arrows
             above it: carrying over is one task shown repeatedly, recurrence
             is a new task each time, and the two must not read as the same
             thing at a glance.
             Indigo, and on a tinted chip rather than a bare gray glyph — this
             is the answer to "why is this here?", so it has to survive a scan
             down a dense list. Same colour as the board card's "Carry over"
             tag; a row has no width for the label, so the icon carries it. -->
        <Tooltip v-if="carriesOverNow" text="Stays in Today every day until you finish it">
          <span
            class="flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-500 dark:text-indigo-300"
            role="img"
            aria-label="Carries over to Today until it is done"
          >
            <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5"/>
            </svg>
          </span>
        </Tooltip>
        <p class="text-[15px] sm:text-sm truncate transition-colors duration-150" :class="isDone ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-800 dark:text-gray-100 sm:group-hover:text-gray-900 dark:sm:group-hover:text-white'">
          {{ task.title }}
        </p>
      </div>
      <p v-if="descriptionPreview" class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{{ descriptionPreview }}</p>
    </div>

    <div class="flex-shrink-0 flex items-center gap-1 sm:gap-1.5" @click.stop>
      <!-- Public link indicator. Deliberately not a button: acting on a share
           belongs on the task's own page, this is only telling you the task
           is out in the world. -->
      <Tooltip v-if="task.shared_publicly" text="Anyone with the link can view this task">
        <span
          class="flex items-center justify-center w-6 h-6 rounded-md text-blue-500 dark:text-blue-400"
          role="img"
          aria-label="Shared publicly"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
          </svg>
        </span>
      </Tooltip>
      <TaskPriorityDropdown :priority="task.priority" @update="(p) => emit('update-priority', task, p)" />
      <TaskDueDateDropdown :due-at="task.due_at" :overdue="task.is_overdue && !isDone" @update="(d) => emit('update-due-date', task, d)" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { stripHtml } from './stripHtml.js'
import { taskTintVars } from './taskColors.js'
import { carriesOver } from './taskCarryOver.js'
import Tooltip from 'controllers/vue_components/shared/Tooltip.vue'
import TaskPriorityDropdown from './TaskPriorityDropdown.vue'
import TaskDueDateDropdown from './TaskDueDateDropdown.vue'

const props = defineProps({
  task: { type: Object, required: true },
  // Only true for the "Today"/"This week" checklist chips — see
  // TasksChecklist.vue and useChecklistOrder.js. Gates whether the drag
  // handle renders at all, not just whether dragging works, so other chips'
  // rows keep their original layout unchanged. Deliberately not named
  // `draggable` — that collides in spirit with the native HTML attribute of
  // the same name and with the imported `vuedraggable` component in the
  // parent template.
  reorderable: { type: Boolean, default: false },
  // True only for the brief window after this row was optimistically added by
  // the checklist's quick-add, which plays its arrival animation. Driven from
  // TasksChecklist.vue rather than inferred from `created_at`, because the row
  // exists before the server has assigned one.
  justCreated: { type: Boolean, default: false },
})
// 'toggle' carries the raw click MouseEvent too — TasksChecklist.vue reads
// its clientX/clientY to anchor the completion confetti burst at the
// checkbox, without this row needing to know anything about that effect.
const emit = defineEmits(['open', 'toggle', 'update-priority', 'update-due-date'])

const isDone = computed(() => props.task.status === 'completed')
const descriptionPreview = computed(() => stripHtml(props.task.description))
// Only while the flag is actually in effect — see carriesOver().
const carriesOverNow = computed(() => carriesOver(props.task))

// A very light wash of the task's own color across the whole row, so the list
// scans by color the way the board's cards and this row's checkbox already do.
// Shared with TaskCard.vue — see `taskTintVars` for the alphas and why this
// arrives as custom properties rather than a ready-made background.
const tintStyle = computed(() => taskTintVars(props.task.color_css_code))

// Hover fills the circle with a soft preview of the task's own color before
// it's actually completed — a visual hint that clicking commits to that
// color, not just a generic checkmark.
const hovering = ref(false)
const checkboxStyle = computed(() => {
  const color = props.task.color_css_code || '#2563eb'
  return {
    backgroundColor: isDone.value ? color : hovering.value ? `${color}26` : 'transparent',
    borderColor: color,
    boxShadow: isDone.value ? `0 0 0 3px ${color}22` : 'none',
  }
})
</script>

<style scoped>
/* The row's color wash — see `tintStyle` for where the custom properties come
   from and why the alpha is what it is.

   `background-color`, never the `background` shorthand: the drag ghost/chosen
   states live in TasksChecklist.vue and set the shorthand, and keeping this to
   the longhand means those stay a clean single override during a drag rather
   than layering on top of the tint. */
.task-row {
  background-color: var(--task-tint);
}

/* Hover and press both deepen the same wash rather than switching to a
   neutral gray, so the row keeps its identity while you interact with it.

   The `:not(...)` on both is load-bearing, not decoration. A drag keeps the
   pointer down and inside the row for its whole duration, so the dragged row
   is `:hover` and `:active` throughout — and TasksChecklist.vue's
   `.task-row.task-row-chosen` / `.task-row-ghost` rules, which paint the drag
   states, would otherwise be competing with these on equal or lower
   specificity. Excluding the two drag classes means these simply stop
   matching while a drag is in progress, so there is nothing to contend with.

   The breakpoint mirrors the `sm:hover:` variant this replaced: hover styling
   starts at sm, so a tap on mobile doesn't leave a row stuck looking hovered
   after the finger lifts. */
@media (min-width: 640px) {
  .task-row:hover:not(.task-row-chosen, .task-row-ghost) {
    background-color: var(--task-tint-hover);
  }
}

.task-row:active:not(.task-row-chosen, .task-row-ghost) {
  background-color: var(--task-tint-hover);
}

/* Arrival animation for a row the quick-add just created.

   Two things run at once. The row itself unrolls: it grows from no height to
   its own, sliding down from under the add row it was typed into, so the rows
   below are pushed apart rather than jumped. And the color wash arrives hot —
   starting well above the resting tint and settling back down to it — which
   makes the new row announce itself in its own color and then quietly join
   the list.

   `max-height` rather than `height` because a row has no fixed height (a
   description preview adds a second line); 6rem is comfortably above the
   tallest a row gets, and since the easing lands the last stretch quickly the
   overshoot isn't visible as a pause.

   An `animation`, not a Vue `<Transition>`: the reorderable scopes render
   through <draggable>, which cannot be wrapped in a TransitionGroup in this
   version (see TasksChecklist.vue), so a transition-based entry would have
   worked on some chips and silently done nothing on Today and This week —
   the two that matter most. A class-driven keyframe animation works
   identically in both branches. */
@keyframes task-row-created {
  0% {
    opacity: 0;
    max-height: 0;
    transform: translateY(-6px);
    background-color: var(--task-tint-strong);
  }
  55% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 1;
    max-height: 6rem;
    transform: none;
    background-color: var(--task-tint);
  }
}

.task-row-created {
  animation: task-row-created 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
  overflow: hidden;
}

/* Anyone who has asked for less motion still gets the color settle, which
   carries the same "this one is new" meaning with nothing actually moving. */
@keyframes task-row-created-tint {
  from { background-color: var(--task-tint-strong); }
  to { background-color: var(--task-tint); }
}

@media (prefers-reduced-motion: reduce) {
  .task-row-created {
    animation: task-row-created-tint 0.6s ease both;
    overflow: visible;
  }
}

/* Bounce-in for the checkmark — status flips optimistically (see
   TasksChecklist.vue's toggle()), so this plays the instant the click
   happens, not after the PATCH resolves. */
.check-pop-enter-active {
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.15s ease;
}
.check-pop-enter-from {
  transform: scale(0);
  opacity: 0;
}
.check-pop-leave-active {
  transition: transform 0.15s ease, opacity 0.15s ease;
}
.check-pop-leave-to {
  transform: scale(0);
  opacity: 0;
}
</style>
